import { PasswordInput, PasswordRequirements } from '_components/atoms';
import { isPasswordValid } from '_helpers/passwordValidation';
import { useAppDispatch } from '_hooks';
import { HelperMethods } from '_utils';
import {
  confirmSignUp,
  resendSignUpCode,
  signIn,
  signUp,
} from 'aws-amplify/auth';
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const returnTo = location.state?.returnTo || '/';
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error when user types
    setSuccess(''); // Clear success message when user types
  };

  const validateForm = () => {
    if (
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.firstName ||
      !formData.lastName
    ) {
      setError('All fields are required');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!isPasswordValid(formData.password)) {
      setError('Password does not meet the requirements');
      return false;
    }
    return true;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Generate a unique username using timestamp and random string
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      const username = `user_${timestamp}_${randomString}`;

      await signUp({
        username, // Use generated username instead of email
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            given_name: formData.firstName,
            family_name: formData.lastName,
          },
        },
      });

      // Store the username for verification
      localStorage.setItem('temp_username', username);
      setVerificationSent(true);
      setSuccess('Verification code sent to your email');
    } catch (error: any) {
      console.error('Error signing up:', error);
      setError(error.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const username = localStorage.getItem('temp_username');
      if (!username) {
        throw new Error('Username not found');
      }

      await confirmSignUp({
        username,
        confirmationCode: verificationCode,
      });

      // After successful verification, sign in the user
      try {
        const { email, password } = formData;

        // Clean up stored username
        localStorage.removeItem('temp_username');

        // Sign in the user directly using AWS Amplify
        await signIn({
          username: email,
          password,
        });

        await HelperMethods.checkUserSession(dispatch);
        // Navigate to the return path or home page after successful sign in
        navigate(returnTo, { replace: true });
      } catch (signInError: any) {
        console.error('Error signing in after verification:', signInError);
        setError(
          'Verification successful, but sign in failed. Please try signing in manually.',
        );
        setVerificationSent(false); // Reset to allow retry
      }
    } catch (error: any) {
      console.error('Error confirming sign up:', error);
      setError(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = async () => {
    try {
      const username = localStorage.getItem('temp_username');
      if (!username) {
        throw new Error('Username not found');
      }

      await resendSignUpCode({
        username,
      });
      setSuccess('Verification code resent successfully');
      setError('');
    } catch (error: any) {
      console.error('Error resending code:', error);
      setError(error.message || 'Failed to resend verification code');
    }
  };

  const isFormValid = useMemo(() => {
    const allFieldsFilled =
      formData.email &&
      formData.password &&
      formData.confirmPassword &&
      formData.firstName &&
      formData.lastName;
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

    return (
      allFieldsFilled &&
      isPasswordValid(formData.password) &&
      formData.password === formData.confirmPassword &&
      isValidEmail
    );
  }, [formData]);

  // Add this validation check
  const isVerificationFormValid = useMemo(() => {
    return verificationCode.length >= 6; // Typically verification codes are 6 digits
  }, [verificationCode]);

  return (
    <div className="items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="text-center text-xl font-bold text-gray-900">
            {verificationSent ? 'Verify' : 'Create Account'}
          </h2>
        </div>

        {!verificationSent ? (
          <>
            {/* <div>
              <GoogleButton />
            </div>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">
                  {'Or continue with'}
                </span>
              </div>
            </div> */}

            <form className="space-y-6" onSubmit={handleSignUp}>
              {error && (
                <div className="text-red-500 text-center text-sm">{error}</div>
              )}
              {success && (
                <div className="text-green-500 text-center text-sm">
                  {success}
                </div>
              )}

              <div className="rounded-md shadow-sm">
                <div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    className="appearance-none rounded-t-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={'First Name'}
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={'Last Name'}
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                    placeholder={'Email'}
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <PasswordInput
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={'Password'}
                    className="rounded-none"
                  />
                </div>
                <div>
                  <PasswordInput
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={'Confirm Password'}
                    className="rounded-b-md"
                  />
                </div>
                <div className="mt-4">
                  <PasswordRequirements
                    password={formData.password}
                    confirmPassword={formData.confirmPassword}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                    isFormValid
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                    loading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading ? 'Loading...' : 'Sign Up'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleVerification}>
            {error && (
              <div className="text-red-500 text-center text-sm">{error}</div>
            )}
            {success && (
              <div className="text-green-500 text-center text-sm">
                {success}
              </div>
            )}

            <div>
              <input
                type="text"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder={'Verification Code'}
                required
              />
            </div>

            <div className="flex flex-col space-y-3">
              <button
                type="submit"
                disabled={loading || !isVerificationFormValid}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isVerificationFormValid
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Loading...' : 'Verify'}
              </button>

              <button
                type="button"
                disabled={loading}
                onClick={resendVerificationCode}
                className={`text-sm ${
                  loading
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-500'
                }`}
              >
                {'Resend Code'}
              </button>
            </div>
          </form>
        )}

        <div className="text-center">
          <p className="text-sm text-gray-600">
            {'Already have an account?'}{' '}
            <button
              onClick={() => navigate('/sign-in', { state: { returnTo } })}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              {'Sign In'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
